// @ts-strict-ignore
import keyBy from 'lodash/keyBy';
import { getActionServiceAttribute } from '../thresholds';
import type {
  Action,
  DataRoute,
  PopulatedDataRoute,
  SchemaInstance,
  WorkflowProps,
} from '../../store/engine/types';
import { extractValuesByKey } from '../util';
import type { DataRoutingCategories } from './types';

export function extractDataRouteIds(
  expression: SchemaInstance['actions'][string]['output']['expression'],
): string[] {
  const DATA_ROUTE_ID_KEY = 'data_route_id';
  const dataRouteIds = extractValuesByKey(expression, DATA_ROUTE_ID_KEY);
  return dataRouteIds;
}


export function isDataRouteAnMLA(dataRoute: DataRoute): boolean {
  return dataRoute.attribute.multiLevelAttributeFilter?.filterComponents?.length > 0;
}

export function populateDataRouteData({
  actions,
  services,
  dataRoute,
  universalAttributes,
}: {
  actions: SchemaInstance['actions'];
  services: WorkflowProps['services'];
  dataRoute: DataRoute;
  universalAttributes: WorkflowProps['universalAttributes'];
}): PopulatedDataRoute {
  const { action, service, attribute } = getActionServiceAttribute({
    actions,
    actionId: dataRoute.sourceId,
    attributeId: dataRoute.attribute?.id,
    services,
    universalAttributes,
  });

  return {
    ...dataRoute,
    sourceAction: action,
    attribute: {
      ...dataRoute.attribute,
      service,
      details: attribute,
    },
  };
}

export function getIncomingDataRoutingCategories({
  action,
  dataRoutes,
  services,
}: {
  action: Action;
  dataRoutes: SchemaInstance['dataRoutes'];
  services: WorkflowProps['services'];
}): DataRoutingCategories[] {
  const selectedActionCategories = services[action.service.internalName].categories;

  if (!selectedActionCategories.request) return null;
  return selectedActionCategories.request
    .map(category => {
      const definedDataRoute = Object.values(dataRoutes).find(route => (
        route.targetId === action.publicId && route.category.id === category.category_id
      ));

      return definedDataRoute ? {
        category,
        dataRoute: definedDataRoute,
      } : {
        category,
      };
    });
}

export function getOutgoingDataRoutingCategories({
  action,
  services,
  dataRoutes,
}: {
  action: Action;
  services: WorkflowProps['services'];
  dataRoutes: SchemaInstance['dataRoutes'];
}): DataRoutingCategories[] {
  const selectedActionCategories = services[action.service.internalName].categories;

  if (!selectedActionCategories.response) return null;
  return selectedActionCategories.response
    .map(category => {
      const definedDataRoute = Object.values(dataRoutes).find(route => (
        route.sourceId === action.publicId && route.category.id === category.id
      ));

      return definedDataRoute ? {
        category,
        dataRoute: definedDataRoute,
      } : {
        category,
      };
    });
}

// Whether this node already has a legacy category route configured on either side - Flow.js
// bypasses legacy routing entirely for a node once it has any dynamic route, so existing
// legacy routes must keep the legacy UI (see ServiceDataRoutingTab).
// A backfilled route (with a target_payload_key) is already dynamic - it has no Deactivate button.
export function hasLegacyDataRoutesConfigured({
  incomingCategories,
  outgoingCategories,
}: {
  incomingCategories: DataRoutingCategories[] | null;
  outgoingCategories: DataRoutingCategories[] | null;
}): boolean {
  return Boolean(
    incomingCategories?.some(({ dataRoute }) => dataRoute && !dataRoute.target_payload_key)
    || outgoingCategories?.some(({ dataRoute }) => dataRoute && !dataRoute.target_payload_key),
  );
}

// Which side of a route a node sits on - incoming means it's the target, outgoing the source.
export function isNodeOnRouteSide({
  mode,
  dataRoute,
  actionId,
}: {
  mode: 'incoming' | 'outgoing';
  dataRoute: Pick<DataRoute, 'sourceId' | 'targetId'>;
  actionId: Action['publicId'];
}): boolean {
  return mode === 'incoming' ? dataRoute.targetId === actionId : dataRoute.sourceId === actionId;
}

export function getDataRoutesForActions(args: {
  action: Action;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): DataRoute[];
export function getDataRoutesForActions(args: {
  actions: Action[];
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): DataRoute[];
export function getDataRoutesForActions(args: {
  action?: Action;
  actions?: Action[];
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): DataRoute[] {
  if (args.action && args.actions) {
    throw TypeError('action and actions are mutually exclusive');
  }

  const actions = args.action ? [args.action] : args.actions;
  return actions.flatMap(action => (
    Object.values(args.dataRoutes).filter(dataRoute => (
      dataRoute.targetId === action.publicId
    ))
  ));
}


export function indexDataRoutesByPublicId(dataRoutes: DataRoute[]) {
  return keyBy(dataRoutes, 'publicId');
}

// This action's dynamic (target_payload_key) routes on either side - unlike
// getDataRoutesForActions (target-only), a route from Iovation to a List shows under both
// nodes' Custom Routes.
export function getDynamicDataRoutesForAction({
  action,
  dataRoutes,
}: {
  action: Action;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): DataRoute[] {
  return Object.values(dataRoutes).filter(dataRoute => (
    (dataRoute.targetId === action.publicId || dataRoute.sourceId === action.publicId)
    && Boolean(dataRoute.target_payload_key)
  ));
}

// The route already occupying draft's (targetId, target_payload_key) slot, if any - callers
// should remove it before saving draft so the picked key overwrites rather than duplicates.
export function findCollidingDynamicDataRoute({
  dataRoutes,
  draft,
}: {
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  draft: Pick<DataRoute, 'publicId' | 'targetId' | 'target_payload_key'>;
}): DataRoute | undefined {
  if (!draft.target_payload_key) return undefined;

  return Object.values(dataRoutes).find(other => (
    other.publicId !== draft.publicId
    && other.targetId === draft.targetId
    && other.target_payload_key === draft.target_payload_key
  ));
}
